import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Box,
  Button,
  CssBaseline,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import { REDIRECT_TIMER, TOAST_SETTINGS } from '../../utility/constants'
import { useNavigate } from 'react-router-dom'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { authService } from '../../services/authService'

import './Signup.css'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { isValidEmail } from '../../utility/validator'
import WaveBackground from './components/WaveBackground'
import { Container } from '@mui/material'

const ResetPassword = () => {
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [emailError, setEmailError] = useState('')
  const [otpError, setOtpError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [stage, setStage] = useState(1)
  const [isEmailValid, setIsEmailValid] = useState(false)
  const [isResetPasswordButtonDisabled, setIsResetPasswordButtonDisabled] = useState(false)
  const [isSticky, setIsSticky] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [isSendingOtp, setIsSendingOtp] = useState(false)

  const handleClickShowPassword = () => setShowPassword((show) => !show)

  const handleScroll = () => {
    if (window.scrollY > 50) {
      setIsSticky(true)
    } else {
      setIsSticky(false)
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleEmailChange = async (e) => {
    //reset error message
    setEmailError('')

    const email = e?.target?.value?.toLowerCase()
    setEmail(email)

    if (isValidEmail(email)) {
      const result = await authService.isUserExist(email, null)
      setIsEmailValid(result.exists)

      if (!result.exists) {
        setEmailError("Email doesn't exist!")
      }
    }
  }

  const handleOTPChange = async (otp) => {
    setOtpError('')
    setOtp(otp)

    if (otp.length == 6) {
      const res = await authService.verifyOtp(email, otp)
      if (res.status) {
        setStage(3)
      } else {
        setOtpError(res.message)
      }
    }
  }

  const handleSendOtp = async (e) => {
    e.preventDefault()
    setIsSendingOtp(true)
    setEmailError('')
    try {
      const res = await authService.sendOtp(email)
      if (res.status) {
        setStage(2)
      } else {
        setEmailError(res.message)
      }
    } catch (error) {
      setEmailError('Something went wrong. Please try again.')
    } finally {
      setIsSendingOtp(false)
    }
  }

  const handleVerifyOtp = async (e) => {
    e.preventDefault()
    const res = await authService.verifyOtp(email, otp)
    if (res.status) {
      setStage(3)
    } else {
      setOtpError(res.message)
    }
  }

  const handleResetPassword = async (e) => {
    e.preventDefault()
    setPasswordError('')
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters long.')
      return
    }
    if (
      !/[A-Z]/.test(password) ||
      !/[a-z]/.test(password) ||
      !/[0-9]/.test(password) ||
      !/[!@#$%^&*]/.test(password)
    ) {
      setPasswordError('Password must contain uppercase, lowercase, number, and special character.')
      return
    }

    try {
      const result = await authService.resetPassword(email, password, otp)
      if (result.status === true) {
        toast.success('Password reset successfully.', TOAST_SETTINGS)
        setIsResetPasswordButtonDisabled(true)
        setTimeout(() => navigate('/login'), REDIRECT_TIMER)
      } else {
        setPasswordError(result.message || 'An unexpected error occurred. Please try again.')
      }
    } catch (err) {
      console.error(err)
      setPasswordError(err?.response?.data?.message || 'Something went wrong. Please try again.')
    }
  }

  return (
    <Box
      className="landing-page"
      style={{
        padding: 24,
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        backgroundColor: '#fdfdfd',
      }}
    >
      <WaveBackground />

      <nav className={`navbar navbar-expand-lg navbar-light ${isSticky ? 'sticky' : 'notify-bx'}`}>
        <Container maxWidth="lg">
          <div className="navbar-brand" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src="/branding/e7_logo_black.png" alt="epoch7 logo" style={{ width: '109px' }} />
          </div>
        </Container>
      </nav>

      <CssBaseline />
      <Box
        className="onboardingForm"
        style={{
          margin: 0,
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          background: ' #fff',
          borderRadius: 12,
          boxShadow: '0 24px 64px #26214a1a',
          padding: '60px 60px',
          width: 500,
          zIndex: 1,
        }}
      >
        {stage === 1 && (
          <Box
            component="form"
            onSubmit={handleSendOtp}
            style={{
              width: '100%',
            }}
          >
            <Typography
              variant="h5"
              color="textSecondary"
              style={{
                color: '#27104E',
                fontSize: '21px',
                marginBottom: '10px',
                fontWeight: '800',
                textAlign: 'center',
              }}
            >
              Reset Password
            </Typography>
            <Typography
              style={{
                marginBottom: '30px',
                color: '#4E62BB',
                fontSize: '12px',
                textAlign: 'center',
              }}
            >
              Please enter your email and we'll send you a 6-digit code.
            </Typography>
            <TextField
              autoFocus
              onChange={handleEmailChange}
              type="email"
              label="Email address"
              variant="outlined"
              color="primary"
              fullWidth
              value={email}
              className="inputsout"
              size="small"
            />
            {emailError && (
              <Typography
                variant="caption"
                color="text.error"
                textAlign="center"
                style={{ marginBottom: 12 }}
              >
                {emailError}
              </Typography>
            )}
            <Button
              className="onboarding-btn"
              fullWidth
              color="primary"
              variant="contained"
              type="submit"
              disabled={!isEmailValid || isSendingOtp}
              onClick={handleSendOtp}
            >
              {isSendingOtp ? 'Sending OTP...' : 'Send OTP'}
            </Button>
            <Typography
              style={{ marginTop: 24, fontSize: 14, cursor: 'pointer', textAlign: 'center' }}
              color="primary"
              onClick={() => navigate('/login')}
            >
              Got your password? <b>Log in instead.</b>
            </Typography>
          </Box>
        )}
        {stage === 2 && (
          <Box component="form" onSubmit={handleVerifyOtp}>
            <Typography
              variant="h5"
              color="textSecondary"
              style={{
                color: '#27104E',
                fontSize: '21px',
                marginBottom: '10px',
                fontWeight: '800',
                textAlign: 'center',
              }}
            >
              Enter OTP
            </Typography>
            <Typography
              style={{
                marginBottom: '30px',
                color: '#4E62BB',
                fontSize: '12px',
                textAlign: 'center',
              }}
            >
              We've sent a 6-digit verification code to your email <b>{email}</b>.
            </Typography>
            <MuiOtpInput
              autoFocus
              style={{ marginBottom: 24 }}
              length={6}
              value={otp}
              onChange={(otp) => handleOTPChange(otp)}
            />
            {otpError && (
              <Typography
                variant="caption"
                color="text.error"
                textAlign="center"
                style={{ marginBottom: 12 }}
              >
                {otpError}
              </Typography>
            )}
            <Button
              fullWidth
              className="onboarding-btn"
              style={{ marginTop: '1rem' }}
              color="primary"
              variant="contained"
              onClick={handleVerifyOtp}
              type="submit"
              disabled={isResetPasswordButtonDisabled}
            >
              Verify OTP
            </Button>
          </Box>
        )}
        {stage === 3 && (
          <Box component="form" onSubmit={handleResetPassword}>
            <Typography
              variant="h5"
              color="textSecondary"
              style={{
                color: '#27104E',
                fontSize: '21px',
                marginBottom: '10px',
                fontWeight: '800',
                textAlign: 'center',
              }}
            >
              Create New Password
            </Typography>
            <Typography
              style={{
                marginBottom: '30px',
                color: '#4E62BB',
                fontSize: '12px',
                textAlign: 'center',
              }}
            >
              Please choose a password that hasn't been used before. Must be at least 8 characters
            </Typography>
            <TextField
              onChange={(e) => setPassword(e.target.value)}
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              color="primary"
              fullWidth
              value={password}
              size="small"
              className="inputsout"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              onChange={(e) => setPassword2(e.target.value)}
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              color="primary"
              fullWidth
              value={password2}
              size="small"
              className="inputsout"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {passwordError && (
              <Typography
                variant="caption"
                color="text.error"
                textAlign="center"
                style={{ marginBottom: 12 }}
              >
                {passwordError}
              </Typography>
            )}
            <Button
              fullWidth
              className="onboarding-btn"
              style={{ marginTop: '1rem' }}
              color="primary"
              variant="contained"
              onClick={handleResetPassword}
              type="submit"
              disabled={password != password2 || password == ''}
            >
              Reset Password
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default ResetPassword
